import React, { FunctionComponent, useState, useEffect } from "react";
import { Typography, styled } from "@mui/material";
import { connect } from "react-redux";

import Vendor from "@components/Vendor";
import Attributes from "./components/Attributes";
import { AppState } from "@store";

import { IDetailsLanguage } from "@ducks/config/types";
import { IItem } from "@ducks/listing/types";

import "./index.css";

interface IContentProps {
  details: IItem | null;
  language: IDetailsLanguage;
}

const Title = styled("div")(({ theme }) => ({
  margin: "1rem 0",
  "& h1": {
    fontSize: "1.5rem",
    lineHeight: "1.875rem",
    marginBottom: "0.5rem",
    [theme.breakpoints.up("md")]: {
      fontSize: "1.5rem",
      lineHeight: "1.875rem",
    },
  },
}));

const AttributesContainer = styled("div")(({ theme }) => ({
  margin: "2rem 0",
  [theme.breakpoints.up("md")]: {
    margin: "2.5rem 0",
  },
  "& a": {
    color: theme.palette.primary.main,
  },
  fontSize: "1rem",
  lineHeight: "1.375rem",
}));

const Description = styled("div")(({ theme }) => ({
  fontSize: "1rem",
  margin: "2rem 0",
  [theme.breakpoints.up("md")]: {
    margin: "2.5rem 0",
  },
  "& a": {
    color: theme.palette.primary.main,
  },
  "& h2": {
    marginBottom: "0.5rem",
    fontSize: "1rem",
    fontWeight: 600,
    lineHeight: "19px",
  },
  "& ul": {
    paddingLeft: "1rem",
    listStyle: "disc",
  },
  "& li": {
    margin: "0.5rem 0",
  },
  "& strong": {
    fontWeight: 600,
  },
}));

const Content: FunctionComponent<IContentProps> = (props) => {
  const { details: d } = props;
  const [formattedDescription, formatDescription] = useState<string>(
    d ? d.description : ""
  );

  useEffect(() => {
    if (d) {
      const regex = RegExp("<(a)([^>]+)>", "g");
      let formattedString = d.description;
      let match;
      while ((match = regex.exec(formattedString)) !== null)
        formattedString = formattedString.replace(
          match[0],
          `<a${match[2]} target="_blank" rel="noopener">`
        );
      formatDescription(formattedString);
    }
  }, [d]);

  return d ? (
    <>
      <Title>
        <Vendor
          itemType={d.type}
          logo={d.vendor.logo}
          name={d.vendor.name}
          redeemType={d.redeemType}
        />
      </Title>
      <Title>
        <Typography variant={"h1"}>{d.title}</Typography>
        <Typography paragraph>{d.highlight}</Typography>
      </Title>
      <AttributesContainer>
        <Attributes details={d} />
      </AttributesContainer>
      <Description>
        <main id="detailsContainer">
          <div dangerouslySetInnerHTML={{ __html: formattedDescription }} />
        </main>
      </Description>
    </>
  ) : null;
};

Content.displayName = "Content";

const mapStateToProps = (state: AppState) => ({
  language: state.config.language.details,
});

export default connect(mapStateToProps)(Content);
